import React, { forwardRef, useCallback, useEffect, useMemo, useState, } from 'react';
import { useInView } from 'react-intersection-observer';
const timingFunctions = {
    linear: 'cubic-bezier(.250, .250, .750, .750)',
    ease: 'cubic-bezier(.250, .100, .250, 1)',
    'ease-in': 'cubic-bezier(.420, 0, 1, 1)',
    'ease-out': 'cubic-bezier(.000, 0, .580, 1)',
    'ease-in-out': 'cubic-bezier(.420, 0, .580, 1)',
    'ease-in-back': 'cubic-bezier(.6, -.28, .735, .045)',
    'ease-out-back': 'cubic-bezier(.175, .885, .32, 1.275)',
    'ease-in-out-back': 'cubic-bezier(.68, -.55, .265, 1.55)',
    'ease-in-sine': 'cubic-bezier(.47, 0, .745, .715)',
    'ease-out-sine': 'cubic-bezier(.39, .575, .565, 1)',
    'ease-in-out-sine': 'cubic-bezier(.445, .05, .55, .95)',
    'ease-in-quad': 'cubic-bezier(.55, .085, .68, .53)',
    'ease-out-quad': 'cubic-bezier(.25, .46, .45, .94)',
    'ease-in-out-quad': 'cubic-bezier(.455, .03, .515, .955)',
    'ease-in-cubic': 'cubic-bezier(.55, .085, .68, .53)',
    'ease-out-cubic': 'cubic-bezier(.25, .46, .45, .94)',
    'ease-in-out-cubic': 'cubic-bezier(.455, .03, .515, .955)',
    'ease-in-quart': 'cubic-bezier(.55, .085, .68, .53)',
    'ease-out-quart': 'cubic-bezier(.25, .46, .45, .94)',
    'ease-in-out-quart': 'cubic-bezier(.455, .03, .515, .955)',
};
export const Faded = forwardRef(({ as: As = 'div', cascade = false, disabled = false, waitMount = false, duration, threshold = 0.2, className = '', cascadeIncrement, triggerOnce = false, animationName, timingFunction, whenInView = false, delay = 0, style, children, ...rest }, ref1) => {
    const variablesStyle = useMemo(() => {
        if (disabled) {
            return;
        }
        const variablesStyle = { ...style };
        if (cascadeIncrement) {
            variablesStyle['--increment'] = `${cascadeIncrement}ms`;
        }
        if (duration) {
            variablesStyle['--duration'] = `${duration}ms`;
        }
        if (timingFunction) {
            variablesStyle['--easing'] =
                timingFunctions[timingFunction] || timingFunction;
        }
        if (delay) {
            if (!cascade) {
                variablesStyle['--delay'] = delay;
            }
            else {
                variablesStyle['--initial-delay'] = `${delay}ms`;
            }
        }
        if (whenInView && !delay) {
            variablesStyle['--initial-delay'] = `100ms`;
        }
        if (animationName) {
            variablesStyle['--animationName'] = animationName;
        }
        return variablesStyle;
    }, [style, animationName, delay, duration, timingFunction, cascade, disabled]);
    const [inView, setInView] = useState(() => !whenInView && !waitMount ? true : false);
    let { ref: ref2, inView: _inView = true } = useInView({
        skip: !whenInView,
        triggerOnce,
        threshold,
    });
    useEffect(() => {
        if (whenInView) {
            setInView(_inView);
        }
    }, [_inView]);
    useEffect(() => {
        if (waitMount) {
            setInView(true);
        }
    }, []);
    const ref = useCombineRefs(ref1, ref2);
    return (React.createElement(As, { style: variablesStyle, className: disabled
            ? className
            : className +
                (inView
                    ? ` biaf biaf${cascade ? 'Cascade' : 'NonCascade'}`
                    : ' biaf biafHidden'), ref: ref, ...rest }, children));
});
export const FadedText = forwardRef(function FadedText({ as: As = 'div', duration, waitMount, threshold = 0.2, className = '', cascadeIncrement = 100, triggerOnce = false, animationName = 'babyFadeTextUp', timingFunction, whenInView = false, delay = 0, style, children, ...rest }, ref1) {
    function makeAnimated({ inView }) {
        if (typeof children !== 'string') {
            return children;
        }
        const words = children.split(' ').filter(Boolean);
        const nodes = words.map((word, index) => {
            const variablesStyle = {};
            variablesStyle['--text-delay'] = `${delay + cascadeIncrement * index}ms`;
            return (React.createElement("span", { className: inView ? ` biaf biafTextWord` : ' biaf biafHidden', style: variablesStyle, key: index }, index !== words.length - 1 ? word + ' ' : word));
        });
        return nodes;
    }
    const parentStyle = useMemo(() => {
        const variablesStyle = {
            ...style,
        };
        if (duration) {
            variablesStyle['--duration'] = `${duration}ms`;
        }
        if (timingFunction) {
            variablesStyle['--easing'] =
                timingFunctions[timingFunction] || timingFunction;
        }
        if (animationName) {
            variablesStyle['--animationName'] = animationName;
        }
        return variablesStyle;
    }, [style]);
    const [inView, setInView] = useState(() => !whenInView && !waitMount ? true : false);
    let { ref: ref2, inView: _inView = true } = useInView({
        skip: !whenInView,
        triggerOnce,
        threshold,
    });
    useEffect(() => {
        if (whenInView) {
            setInView(_inView);
        }
    }, [_inView]);
    useEffect(() => {
        if (waitMount) {
            setInView(true);
        }
    }, []);
    const ref = useCombineRefs(ref1, ref2);
    const newChildren = useMemo(() => {
        return makeAnimated({ inView });
    }, [
        children,
        inView,
        animationName,
        delay,
        duration,
        timingFunction,
        cascadeIncrement,
    ]);
    return (React.createElement(As, { className: className + ' biafTextWrapper', style: parentStyle, ref: ref, ...rest }, newChildren));
});
function useCombineRefs(...refs) {
    const setRef = useCallback((node) => {
        for (let ref1 of refs) {
            if (typeof ref1 === 'function') {
                ref1(node);
            }
            else if (ref1) {
                ref1.current = node;
            }
        }
    }, [...refs]);
    return setRef;
}
//# sourceMappingURL=index.js.map